import { Component, useEffect, useRef, useState } from "react";
import { BrowserRouter, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import "@/App.css";
import { LogoMark } from "@/components/Logo";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import MarqueeBar from "@/components/MarqueeBar";
import Problema from "@/components/Problema";
import Hidraulica from "@/components/Hidraulica";
import Eletricos from "@/components/Eletricos";
import ConversaoAssistida from "@/components/ConversaoAssistida";
import ComoFunciona from "@/components/ComoFunciona";
import Regioes from "@/components/Regioes";
import ParaQuem from "@/components/ParaQuem";
import Equipe from "@/components/Equipe";
import Avaliacoes from "@/components/Avaliacoes";
import Garantia from "@/components/Garantia";
import Faq from "@/components/Faq";
import CtaFinal from "@/components/CtaFinal";
import Footer from "@/components/Footer";
import StickyCta from "@/components/StickyCta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-paper p-8 text-center">
          <p className="font-display text-xl font-bold text-[#0A5C8C]">Algo saiu do fluxo. Recarregue a página.</p>
        </div>
      );
    }
    return this.props.children;
  }
}

function AuthCallback({ onUser }) {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = window.location.hash.split("session_id=")[1]?.split("&")[0];
    (async () => {
      try {
        if (sessionId) {
          const res = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
            credentials: "include",
          });
          if (res.ok) onUser(await res.json());
        }
      } finally {
        navigate("/", { replace: true });
      }
    })();
  }, [navigate, onUser]);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-paper">
      <LogoMark className="h-14 w-14 animate-pulse" />
      <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#0A5C8C]">Entrando...</p>
    </div>
  );
}

function AppShell() {
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.11, smoothWheel: true });
    window.__lenis = lenis;
    let raf = 0;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) return;
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("unauthenticated"))))
      .then(setUser)
      .catch(() => setUser(null));
  }, []);

  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onUser={setUser} />;
  }

  return (
    <div className="app-root overflow-x-clip bg-paper text-[#1A1A1A]">
      <Navbar user={user} onLogout={() => setUser(null)} />
      <main>
        <Hero />
        <MarqueeBar />
        <Problema />
        <Hidraulica />
        <Eletricos />
        <ConversaoAssistida />
        <ComoFunciona />
        <Regioes />
        <ParaQuem />
        <Equipe />
        <Avaliacoes />
        <Garantia />
        <Faq />
        <CtaFinal />
      </main>
      <Footer />
      <StickyCta />
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppShell />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
